import { useEffect } from 'react'
import { LuTrash2 } from 'react-icons/lu'
import { getServiceLabel } from '../utilities/service'
import useAccessGrants from '../models/useAccessGrants'
import useNode from '../models/useNode'
import useOrganizations from '../models/useOrganizations'
import { useOrgRole } from '../hooks/useOrgRole'
import { getUserId } from '../lib/workos'
import { mayChangeService } from '../utilities/orgRole'
import { roleInOrg } from '../utilities/nodeTenant'
import Alert from '@mui/material/Alert'
import Typography, { TypographyProps } from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Modal from './Modal'
import UserLabel from './UserLabel'
import useOrgMembers from '../models/useOrgMembers'
import { withRosterName } from '../utilities/userLabel'

type Props = {
  services: Service[]
  // What to say when there is nothing to show. Omitted renders nothing, for
  // callers that already gate on having services.
  empty?: string
  // The modal gives this its own heading, so service names sit at h6 there; in
  // the node page's Grants section they are a level deeper.
  titleVariant?: TypographyProps['variant']
}

// Who may reach the services a node publishes. This is the human-facing half of
// a node's grants and a different authority from the node's own MCP grant
// (NodeGrant): emails on services.authorized_emails, changed through the router's
// `service_share` (app#373). Only the node's controllers — its owner, or an
// Owner/Admin of the node's org — answer requests or change the list; everyone else sees it read-only.
//
// The store is kept hydrated and live by useAccessRequestNotifier (mounted in
// Layout, so on every authenticated page), which is why this renders from the
// store and fetches nothing itself.
export default function SharedAccessGrants({ services, empty, titleVariant = 'h6' }: Readonly<Props>) {
  const { grants, approve, reject, revoke, error, rootApproval } = useAccessGrants()
  const nodes = useNode(s => s.nodes)
  const { controls } = useOrgRole()
  const organizations = useOrganizations(s => s.organizations)
  // A service belongs to its node, so the node's controllers change who may visit it: its owner,
  // or an Owner/Admin of the node's TENANT, `node.ownerOrgId` (G-1, app#385; Benoit, 2026-09-26).
  // A node not loaded keeps the active org's answer, as before.
  const controlsNodeOf = (nodeId: string | null | undefined): boolean => {
    const node = nodes.find(n => n.id === nodeId)
    if (!node) return controls(undefined)
    return mayChangeService(roleInOrg(organizations, node.ownerOrgId), node.ownerUserId, getUserId())
  }
  // ⚠ The store is shared by every page, so a refusal is cleared when this panel goes away, or it
  // shows on the next node's panel under services it is not about. The same holds for a root
  // confirmation that nobody answered. ⚠ Cleared when the panel OPENS too: an answer that arrives
  // after the panel closed sets them again, and the next panel must not open a dialog nobody asked for.
  useEffect(() => {
    const clear = () => useAccessGrants.getState().set({ error: null, rootApproval: null })
    clear()
    return clear
  }, [])
  const members = useOrgMembers(state => state.members)
  // A visitor is an email. The name comes from the active org's member list when the reader has it
  // (an Owner or Admin); otherwise the email shows alone (app#401). A pattern (`*@acme.com`) shows as
  // typed. ⚠ No "not a member" mark here: that needs the router's `member` fact (router#1841), and a
  // miss in the ACTIVE org's list does not prove the person is outside the NODE's org.
  const person = (email: string) => withRosterName({ email }, members)

  // The root confirmation belongs to the panel that shows the request's service, and it follows
  // the LIVE request: when another controller grants it, rejects it or deletes it, the dialog
  // closes. ⚠ `rejectedAt` is compared with the row the dialog opened for, not with null:
  // "Accept after all" opens the dialog on a rejected row.
  const rootGrant =
    rootApproval &&
    grants.find(
      grant =>
        grant.id === rootApproval.grant.id && !grant.grantedAt && grant.rejectedAt === rootApproval.grant.rejectedAt
    )
  const rootService = rootGrant && services.find(service => service.id === rootGrant.serviceId)
  // ⚠ Also when the service left this panel (the page went to another service with no remount):
  // a confirmation with no dialog would refuse every later root accept as "another is waiting".
  const rootGone = !!rootApproval && !rootService
  useEffect(() => {
    if (rootGone) useAccessGrants.getState().set({ rootApproval: null })
  }, [rootGone])
  const closeRootApproval = () => useAccessGrants.getState().set({ rootApproval: null })

  if (services.length === 0)
    return empty ? (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {empty}
      </Typography>
    ) : null

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}
      {rootApproval && rootGrant && rootService && (
        <RootApprovalDialog
          email={rootGrant.requesterEmail}
          nodeName={nodes.find(n => n.id === rootService.nodeId)?.name}
          outsideOrg={rootApproval.outsideOrg}
          onCancel={closeRootApproval}
          onConfirm={() => {
            closeRootApproval()
            void approve(rootGrant, true)
          }}
        />
      )}
      {services.map(service => {
        const canChange = controlsNodeOf(service.nodeId)
        const forService = grants.filter(grant => !grant.grantedAt && grant.serviceId === service.id)
        const pending = forService.filter(grant => !grant.rejectedAt)
        // A rejection now persists, and the requester cannot ask again until it lapses — so this is the
        // only way back from a mis-click, and it has to be visible rather than implied. Once it HAS
        // lapsed the requester asks again and gets a second row, so a rejection whose email is already
        // pending is spent: listing both would show the same person twice, one of them as a "no" that
        // no longer holds.
        const rejected = forService.filter(
          grant =>
            grant.rejectedAt &&
            !pending.some(p => p.requesterEmail.toLowerCase() === grant.requesterEmail.toLowerCase())
        )
        const authorized = service.authorizedEmails ?? []
        return (
          <Box key={service.id}>
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Typography variant={titleVariant}>{getServiceLabel(service)}</Typography>
              {pending.length > 0 && (
                <Chip
                  label={pending.length > 1 ? `${pending.length} pending requests` : 'Pending request'}
                  size="small"
                  color="primary"
                />
              )}
            </Stack>
            {/* ⚠ What a name on this list gives (app#461). The list showed a name and a bin, and
                nothing said if the person had access, or to what: a web page, a shell, or root. */}
            <Typography variant="caption" component="div" sx={{ color: 'text.secondary', marginBottom: 0.5 }}>
              {visitorGets(service, nodes.find(n => n.id === service.nodeId)?.name)}
            </Typography>
            {!pending.length && !rejected.length && !authorized.length ? (
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                No users
              </Typography>
            ) : (
              <Stack>
                {pending.map(grant => (
                  <Stack
                    key={grant.id}
                    direction="row"
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      py: 0.5,
                    }}
                  >
                    <UserLabel user={person(grant.requesterEmail)} />
                    {canChange && (
                    <Stack direction="row" sx={{ gap: 1, flexShrink: 0 }}>
                      {/* ⚠ Labelled buttons, not 16 px icons: the answer to a request must be easy
                          to find and to read (app#461). */}
                      <Button
                        size="small"
                        variant="contained"
                        color={givesRoot(service) ? 'error' : 'primary'}
                        onClick={() => void approve(grant)}
                      >
                        Accept
                      </Button>
                      <Button size="small" variant="outlined" color="inherit" onClick={() => void reject(grant)}>
                        Reject
                      </Button>
                    </Stack>
                    )}
                  </Stack>
                ))}
                {rejected.map(grant => (
                  <Stack
                    key={grant.id}
                    direction="row"
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      py: 0.5,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                      <UserLabel user={person(grant.requesterEmail)} sx={{ color: 'inherit' }} /> — rejected
                    </Typography>
                    {canChange && (
                      <Button
                        size="small"
                        variant="outlined"
                        color={givesRoot(service) ? 'error' : 'primary'}
                        sx={{ flexShrink: 0 }}
                        onClick={() => void approve(grant)}
                      >
                        Accept after all
                      </Button>
                    )}
                  </Stack>
                ))}
                {authorized.map(email => (
                  <Stack
                    key={email}
                    direction="row"
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      py: 0.5,
                    }}
                  >
                    <UserLabel user={person(email)} />
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      {listGivesAccess(service) && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Has access
                        </Typography>
                      )}
                      {canChange && (
                        <Tooltip title="Remove access" arrow>
                          <IconButton size="small" onClick={() => revoke(service.id, email)}>
                            <LuTrash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        )
      })}
    </Stack>
  )
}

/**
 * One line that says what a person on a service's list can do (app#461).
 *
 * ⚠ A name on a service gives that service and nothing else on the node. It is not the node's
 * `use` or `root` level, which the access list gives. The one case that gives root is a ROOT
 * terminal: there the name is the root grant (docs/PERMISSIONS.md § 5 Services, D-40).
 * ⚠ `service.admin` is what the app knows. A terminal on a node that offers only root is a root
 * terminal too, and the app cannot see that here (app#404), so the plain terminal line does not
 * name an account.
 */
function visitorGets(service: Service, nodeName?: string): string {
  const node = nodeName ?? 'the node'
  if (!service.enabled) return 'This service is off. Nobody can open it until it is turned on.'
  if (!service.authRequired) return 'Public. Anyone who has the link can open it, so this list changes nothing.'
  if (service.url) return `Web service. A person on this list can open it in a browser, and gets nothing else on ${node}.`
  const runs = service.command ? `runs "${service.command}"` : 'gets a shell'
  if (service.admin) return `Root terminal. A person on this list ${runs} as root on ${node}.`
  return `Terminal. A person on this list ${runs} on ${node}.`
}

/**
 * true when accepting a request on this service gives ROOT: a root terminal. Its Accept button has
 * the error color, like every other button of the app that gives root.
 */
const givesRoot = (service: Service): boolean => service.admin && !service.url

/** "Has access" is true only while the list is what lets a person in. */
const listGivesAccess = (service: Service): boolean => service.enabled && service.authRequired

type RootApprovalDialogProps = {
  email: string
  /** Absent when the node is not loaded: the dialog then says "this node". */
  nodeName?: string
  outsideOrg: boolean
  onCancel: () => void
  onConfirm: () => void
}

/**
 * The question the app asks before it approves a request on a ROOT terminal (app#461).
 *
 * ⚠⚠ A root grant is NEVER silent (docs/PERMISSIONS.md § 4.1, D-3; § 5 Services, D-40): the name
 * on a root terminal is the root grant, and the app sends `confirm_root: true` only from the
 * button here. The dialog must say what root gives, the node's own settings and its key included.
 * ⚠ It is a question, not an error. The router's refusal text is for an API caller: one long
 * paragraph in an error Alert, which a reader closes without reading. So the facts are short
 * lines under three headings, in the page's own colors, and the page never shows that text.
 */
function RootApprovalDialog({ email, nodeName, outsideOrg, onCancel, onConfirm }: Readonly<RootApprovalDialogProps>) {
  const node = nodeName ?? 'this node'
  return (
    <Modal open onClose={onCancel} maxWidth="xs">
      <DialogTitle variant="subtitle1" sx={{ overflowWrap: 'anywhere', paddingRight: 6 }}>
        Give {email} a root terminal?
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ overflowWrap: 'anywhere' }}>
          <Typography variant="body2">
            This terminal runs as root on <strong>{node}</strong> (administrator on Windows). To accept the request,
            confirm that {email} can have it.
          </Typography>
          {outsideOrg && (
            <Alert severity="info">{email} is not a member of the organization that owns this node.</Alert>
          )}
          <RootFacts title="What they get">
            <li>A root shell on {node}, in this terminal only.</li>
            <li>
              Root can read and change everything on the machine. That includes the node’s own noBGP settings and
              its key.
            </li>
          </RootFacts>
          <RootFacts title="What does not change">
            <li>They get no other access to the node through noBGP, and no role in the organization.</li>
          </RootFacts>
          <RootFacts title="How to take it back">
            <li>Remove {email} from this list. The terminal then closes for them.</li>
            <li>
              That does not undo what they changed on the machine, for example an SSH key or a cron job.
            </li>
          </RootFacts>
        </Stack>
      </DialogContent>
      <DialogActions>
        {/* ⚠ Outlined, like Reject on the row: the grey text Cancel of other dialogs is hard to see
            on the dark theme, and "no" must be as easy to find as "yes" here. */}
        <Button variant="outlined" color="inherit" onClick={onCancel}>
          Cancel
        </Button>
        {/* ⚠ The error color, like "Give root" on the node's access list (`GrantForm`): every
            button that gives root has it, so root is seen before the words are read (Benoit,
            2026-10-07). The TEXT of the dialog stays in the page's own colors. */}
        <Button variant="contained" color="error" onClick={onConfirm}>
          Give root terminal
        </Button>
      </DialogActions>
    </Modal>
  )
}

function RootFacts({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <Box>
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="body2" component="ul" sx={{ margin: 0, paddingLeft: 2.5, color: 'text.secondary' }}>
        {children}
      </Typography>
    </Box>
  )
}
